import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { NavLink, withRouter } from 'react-router-dom';
import useAdminNavigationRoutes from '../../hooks/useAdminNavigationRoutes';

const isExternalRouteActive = href => {
  if (!href || typeof window === 'undefined') {
    return false;
  }

  const currentPath = `${window.location.pathname}${window.location.search}`;
  const normalizedHref = href.replace(window.location.origin, '');

  return currentPath === normalizedHref || currentPath.indexOf(normalizedHref) === 0;
};

const normalizeSearch = search => {
  const params = new URLSearchParams(search || '');
  return params.toString();
};

const isRouteActive = (route, location) => {
  const pathname = location.pathname;
  const currentSearch = normalizeSearch(location.search);

  if (route.to) {
    if (route.search) {
      return pathname === route.to && currentSearch === normalizeSearch(route.search);
    }

    if (pathname === route.to || pathname.indexOf(`${route.to}/`) === 0) {
      return true;
    }
  }

  if (route.href && isExternalRouteActive(route.href)) {
    return true;
  }

  return Array.isArray(route.children) && route.children.some(child => isRouteActive(child, location));
};

const hasChildren = route => Array.isArray(route.children) && route.children.length > 0;
const getRouteTarget = route => {
  if (route.href) {
    return route.href;
  }

  if (route.to && route.search) {
    return {
      pathname: route.to,
      search: route.search
    };
  }

  return route.to || null;
};

const NavbarTopMenu = ({ location }) => {
  const [openedMenus, setOpenedMenus] = useState({});
  const topMenuRoutes = useAdminNavigationRoutes();

  useEffect(() => {
    setOpenedMenus({});
  }, [location.pathname]);

  const toggleMenu = key => {
    setOpenedMenus(state => ({
      ...state,
      [key]: !state[key]
    }));
  };

  const renderMenuLink = (route, key, isActive, hasChildren, caretIcon) => {
    const target = getRouteTarget(route);
    const sharedClassName = classNames('med-top-menu-link', { 'is-active': isActive });

    if (hasChildren) {
      return (
        <button
          type="button"
          className={sharedClassName}
          onClick={() => toggleMenu(key)}
          aria-expanded={!!openedMenus[key]}
        >
          <span>{route.name}</span>
          <FontAwesomeIcon icon={caretIcon} className="med-top-menu-link-caret" />
        </button>
      );
    }

    if (route.href) {
      return (
        <a href={target} className={sharedClassName}>
          <span>{route.name}</span>
        </a>
      );
    }

    if (route.to) {
      return (
        <NavLink to={target} className={sharedClassName} exact={route.exact}>
          <span>{route.name}</span>
        </NavLink>
      );
    }

    return (
      <button
        type="button"
        className={sharedClassName}
        aria-expanded={undefined}
      >
        <span>{route.name}</span>
      </button>
    );
  };

  const renderItems = (items, level = 0, trail = 'root') => (
    <ul className={level === 0 ? 'med-top-menu' : 'med-top-submenu'}>
      {items.map((route, index) => {
        const key = `${trail}-${index}`;
        const routeHasChildren = hasChildren(route);
        const isActive = isRouteActive(route, location);
        const isOpen = !!openedMenus[key];
        const caretIcon = level === 0 ? 'chevron-down' : 'angle-right';

        return (
          <li
            key={route.to || `${key}-${route.name}`}
            className={classNames('med-top-menu-item', {
              'has-children': routeHasChildren,
              'is-active': isActive,
              'is-open': isOpen
            })}
          >
            <div className="med-top-menu-row">
              {renderMenuLink(route, key, isActive, routeHasChildren, caretIcon)}
              {routeHasChildren && (
                <button
                  type="button"
                  className="med-top-menu-toggle"
                  onClick={event => {
                    event.preventDefault();
                    event.stopPropagation();
                    toggleMenu(key);
                  }}
                  aria-expanded={isOpen}
                  aria-label={`${route.name} menyusini ochish`}
                >
                  <FontAwesomeIcon icon={caretIcon} />
                </button>
              )}
            </div>

            {routeHasChildren && (
              <div className={classNames('med-top-menu-dropdown', { 'is-open': isOpen })}>
                <div className="med-top-menu-dropdown-card">{renderItems(route.children, level + 1, key)}</div>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );

  return <div className="med-top-menu-wrap">{renderItems(topMenuRoutes)}</div>;
};

NavbarTopMenu.propTypes = {
  location: PropTypes.object.isRequired
};

export default withRouter(NavbarTopMenu);
